import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from "react";
import { Platform } from "react-native";
import { useQueryClient, useIsRestoring } from "@tanstack/react-query";
import { invalidateQueriesFor, tenantsEndpoint } from "@visioni/a3s-dir/app";
import { API } from "@visioni/a3s-dir/client-api";
import { ApiConfigProvider } from "@visioni/a3s-client-api/app";
import type { z } from "zod/v4";
import { useEnvironment } from "@src/config/environment-store";
import { authenticatedFetch } from "../auth";
import { useAuthStore } from "../auth/auth-store";
import { identifyCurrentUserWithAmplitude } from "@src/services/analytics/amplitude";
import {
  useLastUsedTenantId,
  setLastUsedTenantId,
  setLastUsedTenantIdSync,
  setCachedTenantContext,
} from "./last-used-tenant-store";
import { getTenantDisplayName } from "./tenant-utils";
import { setSelectedTenantRealm } from "./tenant-realm-cache";
import { useAppDirectoryTenants } from "./useAppDirectoryTenants";
import { track } from "@visioni/a3s-analytics";
import { setTags } from "@visioni/a3s-error-reporting";
import { TenantSelected, tenantSelectedProps } from "./analytics";

// Types derived from API schema
type TenantsResponse = z.infer<typeof API.me.tenants.Response>;
export type Tenant = TenantsResponse["tenants"][number];

// Query keys that should be invalidated when the selected tenant changes
const tenantScopedKeys = ["cameras", "alarms", "activity", "camera-contacts"] as const;

interface TenantContextValue {
  /** All available tenants for the current user */
  tenants: readonly Tenant[];
  /** Currently selected tenant (derived from URL, last used, or first available) */
  selectedTenant: Tenant | undefined;
  /** Whether tenants are currently being loaded */
  isLoading: boolean;
  /** Error from fetching tenants, if any */
  error: Error | null;
  /** Whether tenant loading has resolved for the current auth state */
  isResolved: boolean;
  selectTenant: (tenantId: string) => void | Promise<void>;
  /** Refetch tenants from the server */
  refreshTenants: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

const VEHICLE_TRACKER_PATH_MARKER = "/features/vehicle-tracker/";

// Local dev only: send vehicle-tracker requests to the local VT dev server, leave everything else on the realm.
const vehicleTrackerProxyBaseUrl = process.env.EXPO_PUBLIC_VT_PROXY_BASE_URL;

const authenticatedFetchWithVehicleTrackerProxy: typeof authenticatedFetch = (url, options) => {
  const markerIndex = url.indexOf(VEHICLE_TRACKER_PATH_MARKER);
  if (!vehicleTrackerProxyBaseUrl || markerIndex === -1) return authenticatedFetch(url, options);
  const pathStart = url.lastIndexOf("/api/", markerIndex);
  return authenticatedFetch(`${vehicleTrackerProxyBaseUrl}${url.slice(pathStart)}`, options);
};

export interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Extract tenant ID from URL path on web.
 * Looks for /tenants/[tenantId]/... pattern.
 *
 * Note: We read directly from window.location rather than using useGlobalSearchParams
 * because TenantProvider renders before expo-router has resolved the current route.
 * On native, +native-intent.tsx handles this by setting lastUsedTenantId before navigation.
 */
function getTenantIdFromUrl(): string | null {
  if (Platform.OS !== "web" || typeof window === "undefined") return null;

  const segments = window.location.pathname.split("/").filter(Boolean);
  const tenantsIndex = segments.indexOf("tenants");

  if (tenantsIndex !== -1 && segments[tenantsIndex + 1]) {
    return segments[tenantsIndex + 1];
  }

  return null;
}

/**
 * Derives the selected tenant based on priority:
 *
 * 1. User-selected tenant ID (if explicitly switched during this session)
 * 2. URL tenant ID (web only) - URL is source of truth on page load
 * 3. Last used tenant ID (if valid) - persisted preference
 * 4. First available tenant - fallback
 */
function deriveSelectedTenant(
  tenants: readonly Tenant[],
  lastUsedTenantId: string | null,
  userSwitchedTenantId: string | null,
): Tenant | undefined {
  if (tenants.length === 0) return undefined;

  // Priority 1: Explicit user selection during this session (overrides URL)
  if (userSwitchedTenantId) {
    const switched = tenants.find((t) => t.id === userSwitchedTenantId);
    if (switched) return switched;
  }

  // Priority 2: URL tenant (web only - for page loads/refreshes)
  const urlTenantId = getTenantIdFromUrl();
  if (urlTenantId) {
    const urlTenant = tenants.find((t) => t.id === urlTenantId);
    if (urlTenant) return urlTenant;
  }

  // Priority 3: Last used tenant (if valid)
  if (lastUsedTenantId) {
    const lastUsedTenant = tenants.find((t) => t.id === lastUsedTenantId);
    if (lastUsedTenant) return lastUsedTenant;
  }

  // Priority 4: First available
  return tenants[0];
}

export function TenantProvider({ children }: TenantProviderProps) {
  const env = useEnvironment();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const lastUsedTenantId = useLastUsedTenantId();
  const [userSwitchedTenantId, setUserSwitchedTenantId] = useState<string | null>(null);

  const isRestoring = useIsRestoring();

  const { data: tenantsResponse, isLoading: isQueryLoading, fetchStatus, error, isFetching } = useAppDirectoryTenants();

  const hasFetchedOnce = useRef(false);
  if (tenantsResponse) hasFetchedOnce.current = true;

  const tenants = useMemo(() => tenantsResponse?.tenants ?? [], [tenantsResponse?.tenants]);

  // Include cache restoration state and first-fetch guard:
  // - isRestoring: during PersistQueryClientProvider hydration, queries report isLoading=false with data=undefined
  // - hasFetchedOnce: prevents the brief frame where isRestoring has cleared but the query observer
  //   hasn't started yet (fetchStatus still 'idle'), which would otherwise report isLoading=false
  //   with zero tenants and flash AccountNotConfiguredView
  const isLoading =
    isQueryLoading || isRestoring || (isFetching && !tenants.length) || (!hasFetchedOnce.current && isAuthenticated);
  // fetchStatus==='paused': query is offline-paused — no settlement coming, so unblock boot.
  const isResolved =
    !isAuthenticated || fetchStatus === "paused" || (!isLoading && (tenantsResponse !== undefined || error !== null));

  // When environment changes, invalidate tenants so we refetch from the new directory
  // (query key does not include env; without this we could show stale tenants and then
  // clear selection when refetch returns, causing "No tenant selected" on native)
  const prevEnvRef = useRef(env);
  useEffect(() => {
    if (prevEnvRef.current.fqdn !== env.fqdn) {
      prevEnvRef.current = env;
      void queryClient.invalidateQueries(invalidateQueriesFor(tenantsEndpoint));
    }
  }, [env, queryClient]);

  // Derive selected tenant and sync the realm cache in one step.
  // apiRequest reads realm synchronously via getSelectedTenantRealm(); child effects fire
  // before parent effects, so the cache must be updated during render — not in useEffect.
  const selectedTenant = useMemo(() => {
    const t = deriveSelectedTenant(tenants, lastUsedTenantId, userSwitchedTenantId);
    setSelectedTenantRealm(t ? `https://${t.realm.fqdn}` : null);
    return t;
  }, [tenants, lastUsedTenantId, userSwitchedTenantId]);

  // Update cache and last used tenant when selection changes
  useEffect(() => {
    if (selectedTenant && selectedTenant.id !== lastUsedTenantId) {
      void setLastUsedTenantId(selectedTenant.id);
    }

    // Cache tenant context for feature flag targeting on next app launch
    if (selectedTenant && tenants.length > 0) {
      void setCachedTenantContext({
        tenantId: selectedTenant.id,
        tenantName: getTenantDisplayName(selectedTenant),
        realm: selectedTenant.realm.fqdn,
        allTenantIds: tenants.map((t) => t.id),
        allRealms: [...new Set(tenants.map((t) => t.realm.fqdn))],
      });
    }
  }, [selectedTenant, lastUsedTenantId, tenants]);

  // Errors carry the selected tenant. A tenant belongs to one realm, so this also
  // tells which realm an error came from.
  const selectedTenantId = selectedTenant?.id;
  useEffect(() => {
    setTags({ tenant_id: selectedTenantId ?? null });
  }, [selectedTenantId]);

  // Re-identify user with Amplitude when tenant context is available (for feature flag targeting)
  const user = useAuthStore((s) => s.user);
  useEffect(() => {
    // Only identify once we have an actual tenants payload from useTenants.
    // This allows legitimate empty-tenant users ([]) while avoiding false tenantCount=0
    // from the `tenantsResponse?.tenants ?? []` loading/error fallback.
    if (user && tenantsResponse) {
      identifyCurrentUserWithAmplitude(user, { selectedTenant, tenants });
    }
  }, [user, tenantsResponse, tenants, selectedTenant]);

  const prevTidRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const tid = selectedTenant?.id;
    if (tid === undefined) return;
    if (prevTidRef.current !== undefined && prevTidRef.current !== tid) {
      tenantScopedKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key], exact: false }));
    }
    prevTidRef.current = tid;
  }, [selectedTenant?.id, queryClient]);

  const selectTenant = useCallback(
    (tenantId: string) => {
      const tenant = tenants.find((t) => t.id === tenantId);
      if (!tenant) return;

      // Tracked before the web reload below; the SDK resends unsent events on the next load.
      const selected = tenantSelectedProps(tenants, selectedTenant?.id, tenantId);
      if (selected) track(TenantSelected, selected);

      if (Platform.OS === "web" && typeof window !== "undefined") {
        // Web switcher mirrors native behaviour: switch tenant, then land on tenant overview.
        // Full navigation keeps URL-derived tenant selection and router state aligned.
        // Preserve the exported web base path (/app) so we stay within SPA routes.
        setLastUsedTenantIdSync(tenantId);
        setUserSwitchedTenantId(tenantId);
        const basePathPrefix =
          window.location.pathname === "/app" || window.location.pathname.startsWith("/app/") ? "/app" : "";
        window.location.pathname = `${basePathPrefix}/tenants/${tenantId}/overview`;
        return;
      }

      setLastUsedTenantIdSync(tenantId);
      setUserSwitchedTenantId(tenantId);
    },
    [tenants, selectedTenant?.id],
  );

  const refreshTenants = async () => {
    await queryClient.invalidateQueries(invalidateQueriesFor(tenantsEndpoint));
  };

  const contextValue: TenantContextValue = {
    tenants,
    selectedTenant,
    isLoading,
    error,
    isResolved,
    selectTenant,
    refreshTenants,
  };

  return (
    <TenantContext.Provider value={contextValue}>
      <ApiConfigProvider
        config={{
          tenant: selectedTenant ? { tenantId: selectedTenant.id, apiBaseUrl: selectedTenant.realm.api } : undefined,
          authenticatedFetch: authenticatedFetchWithVehicleTrackerProxy,
        }}
      >
        {children}
      </ApiConfigProvider>
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 * @throws Error if used outside of TenantProvider
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within TenantProvider");
  }
  return context;
}

/** Current tenant ID, or "" when none is selected. */
export function useTenantId(): string {
  const { selectedTenant } = useTenant();
  return selectedTenant?.id ?? "";
}

/** Convenience hook that signals when tenant data has settled. */
export function useTenantReady() {
  const { tenants, isLoading, error } = useTenant();
  return {
    isReady: !isLoading && !error,
    isEmpty: !isLoading && !error && tenants.length === 0,
    isLoading,
    error,
  };
}
